import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { useAppSelector } from "../../hooks/useAuth";
import { RootState } from "../../store";
import ProtectedRoute from "./ProtectedRoute";
import { MainLayout } from "../layout";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { ROLES } from "../../hooks/useRBAC";
import {
  LoginPage,
  DashboardPage,
  UserManagementPage,
  StaffManagementPage,
  HubManagementPage,
  VehicleManagementPage,
  PlanManagementPage,
  CreatePlanPage,
  EditPlanPage,
  PaymentManagementPage,
  RefundManagementPage,
  BookingManagementPage,
  RideManagementPage,
  SupportPage,
  SettingsPage,
  PromoCodesPage,
  CreatePromoCodePage,
  ReferralSettingsPage,
  EditRefundPage,
} from "../../pages";
import RideDetailsPage from "../../pages/ride-management/ride-details/RideDetailsPage";
import UserDetailsPage from "../../pages/user-management/UserDetailsPage";
import UserProfile from "../../pages/user-profile/UserProfile";
import BookingDetailsPage from "../../pages/booking-management/BookingDetailsPage";
import EditBookingPage from "../../pages/booking-management/EditBookingPage";
import CreateBookingPage from "../../pages/booking-management/CreateBookingPage";
import SupportDetailsPage from "../../pages/support/SupportDetailsPage";
import EditStaffPage from "../../pages/staff-management/Edit-staff/EditStaffPage";
import StaffDetails from "../../pages/staff-management/Staff-Details/StaffDetails";
import EditHubPage from "../../pages/hub-management/Edit-Hub/EditHubPage";
import HubManagementDetails from "../../pages/hub-management/HubDetails/HubManagementDetails";
import VehicleManagementDetails from "../../pages/vehicle-management/VehicleDetails/VehicleManagementDetails";
import EditVehiclePage from "../../pages/vehicle-management/Edit-Vehicle/EditVehiclePage";
import PaymentDetails from "../../pages/payment-management/PaymentDetails";

const DocumentTitleUpdater: React.FC = () => {
  useDocumentTitle();
  return null;
};

const DefaultRouteRedirect: React.FC = () => {
  const { user } = useAppSelector((state: RootState) => state.auth);

  const getDefaultRoute = () => {
    if (!user?.roleid) return "/dashboard";
    switch (user.roleid) {
      case ROLES.SUPPORT:
        return "/booking-management";
      case ROLES.SUPER_ADMIN:
      case ROLES.ADMIN:
      case ROLES.SUPPORT_ADMIN:
      case ROLES.HUB_CLUSTER_MANAGER:
      case ROLES.HUB_MANAGER:
      case ROLES.FINANCE:
      case ROLES.MANAGER:
      default:
        return "/dashboard";
    }
  };

  return <Navigate to={getDefaultRoute()} replace />;
};

const AppRouter: React.FC = () => {
  const { token, loading } = useAppSelector((state: RootState) => state.auth);
  const isAuthenticated = !!token;

  return (
    <Router>
      <DocumentTitleUpdater />
      <Routes>
        <Route
          path="/"
          element={isAuthenticated ? <DefaultRouteRedirect /> : <LoginPage />}
        />

        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route path="/user-management">
            <Route index element={<UserManagementPage />} />
            <Route path="user-details" element={<UserDetailsPage />} />
          </Route>

          <Route path="/user/profile" element={<UserProfile />} />

          <Route path="/staff-management">
            <Route index element={<StaffManagementPage />} />
            <Route path="create-staff" element={<StaffManagementPage />} />
            <Route
              path="edit-staff/:id"
              element={<EditStaffPage onBack={() => window.history.back()} />}
            />
            <Route
              path="staff-details/:id"
              element={<StaffDetails onBack={() => window.history.back()} />}
            />
          </Route>

          <Route path="/hub-management">
            <Route index element={<HubManagementPage />} />
            <Route path="create-hub" element={<HubManagementPage />} />
            <Route
              path="edit-hub/:id"
              element={<EditHubPage onBack={() => window.history.back()} />}
            />
            <Route
              path="hub-details/:id"
              element={
                <HubManagementDetails onBack={() => window.history.back()} />
              }
            />
          </Route>

          <Route path="/vehicle-management">
            <Route index element={<VehicleManagementPage />} />
            <Route path="create-vehicle" element={<VehicleManagementPage />} />
            <Route
              path="edit-vehicle/:id"
              element={<EditVehiclePage onBack={() => window.history.back()} />}
            />
            <Route
              path="vehicle-details/:id"
              element={
                <VehicleManagementDetails
                  onBack={() => window.history.back()}
                />
              }
            />
          </Route>

          <Route path="/plan-management">
            <Route index element={<PlanManagementPage />} />
            <Route
              path="create-plan"
              element={<CreatePlanPage onBack={() => window.history.back()} />}
            />
            <Route
              path="edit-plan/:hubmasterid"
              element={<EditPlanPage onBack={() => window.history.back()} />}
            />
          </Route>

          <Route path="/payment-management">
            <Route index element={<PaymentManagementPage />} />
            <Route
              path="details/:id"
              element={<PaymentDetails onBack={() => window.history.back()} />}
            />
          </Route>

          <Route path="/refund-management">
            <Route index element={<RefundManagementPage />} />
            <Route path="edit-refund/:id" element={<EditRefundPage />} />
          </Route>

          <Route path="/booking-management">
            <Route index element={<BookingManagementPage />} />
            <Route path="create-booking" element={<CreateBookingPage />} />
            <Route path="edit-booking" element={<EditBookingPage />} />
            <Route path="details" element={<BookingDetailsPage />} />
          </Route>

          <Route path="/ride-management">
            <Route index element={<RideManagementPage />} />
            <Route path="ride-details/:id" element={<RideDetailsPage />} />
          </Route>

          <Route path="/support">
            <Route index element={<SupportPage />} />
            <Route path="create-support" element={<SupportPage />} />
            <Route path="details" element={<SupportDetailsPage />} />
          </Route>

          <Route path="/settings" element={<SettingsPage />} />

          <Route
            path="/marketing/promo-codes/create"
            element={<CreatePromoCodePage />}
          />
          <Route path="/marketing/promo-codes" element={<PromoCodesPage />} />
          <Route
            path="/marketing/referral-settings"
            element={<ReferralSettingsPage />}
          />
        </Route>

        <Route
          path="*"
          element={
            isAuthenticated ? (
              <DefaultRouteRedirect />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
    </Router>
  );
};

export default AppRouter;
